import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { PAYMENT_PLAN_INPUT_MODE } from '#/api/srm/serializePaymentPlan';
import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import PurchasePaymentPhasesTable from '#/modules/srmMgmt/purchaseOrder/components/PurchasePaymentPhasesTable';
import {
  PAYMENT_DATA_STATUS,
  PAYMENT_PLAN_INPUT_MODE_OPTIONS,
  createEmptyPaymentPlanPhase,
  hasTotalAmountChanged,
  mapCurrentPlanToFormPhases,
  summarizePaymentPlanPhases,
} from '#/modules/srmMgmt/purchaseOrder/helper/paymentPlanForm';
import { formatDisplayAmount } from '#/utils/formatNumber';

const NEW_PHASE_VALUE = '';

function formatPhaseOption(phase) {
  const amount = phase?.amount == null ? '' : ` ${formatDisplayAmount(phase.amount)}`;
  return `原第 ${phase?.phaseNo ?? '?'} 期${amount}`;
}

/**
 * 異動表單內的付款計畫區塊。勾選「一併調整付款計畫」才會送 `payment_plan`；
 * 應付總額改變時必須勾選並提供完整計畫（合計等於新總額）。
 * 每期可選「承接」目前有效計畫的某一期（已付款期必須承接剛好一次），或作為新期別。
 */
const PurchaseAmendmentPaymentPlanEditor = ({
  control,
  register,
  errors,
  setValue,
  currentPlan,
  currentPlanError,
  originalTotal,
  nextTotal,
  disabled = false,
}) => {
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'paymentPlan.phases',
  });
  const enabled = useWatch({ control, name: 'paymentPlan.enabled' });
  const inputMode = useWatch({ control, name: 'paymentPlan.inputMode' });
  const watchedPhases = useWatch({ control, name: 'paymentPlan.phases', defaultValue: [] });

  const isPercentage = inputMode === PAYMENT_PLAN_INPUT_MODE.PERCENTAGE;
  const totalChanged = hasTotalAmountChanged(originalTotal, nextTotal);
  const summary = summarizePaymentPlanPhases(
    { inputMode, phases: watchedPhases },
    nextTotal,
  );
  const paymentDataUnknown = currentPlan?.paymentDataStatus === PAYMENT_DATA_STATUS.HISTORICAL_UNKNOWN;

  const phaseOptions = useMemo(
    () => [
      { value: NEW_PHASE_VALUE, label: '新期別' },
      ...(currentPlan?.phases ?? []).map((phase) => ({
        value: phase.id,
        label: formatPhaseOption(phase),
      })),
    ],
    [currentPlan?.phases],
  );

  function handleResetFromCurrent() {
    replace(mapCurrentPlanToFormPhases(currentPlan));
  }

  function handleToggle(checked) {
    setValue('paymentPlan.enabled', Boolean(checked), { shouldDirty: true });
    if (checked && fields.length === 0) {
      const phases = mapCurrentPlanToFormPhases(currentPlan);
      replace(phases.length ? phases : [createEmptyPaymentPlanPhase()]);
    }
  }

  return (
    <section className="@container/form-section pb-6">
      <div className="form-block">
        <h2 className="form-block__title">付款計畫</h2>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-xs text-gray-500">目前有效計畫</p>
          {currentPlanError ? (
            <p className="text-sm text-amber-700">{currentPlanError}</p>
          ) : (
            <PurchasePaymentPhasesTable
              phases={currentPlan?.phases ?? []}
              paymentDataUnknown={paymentDataUnknown}
            />
          )}
        </div>

        <Controller
          name="paymentPlan.enabled"
          control={control}
          render={({ field }) => (
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <Checkbox
                checked={Boolean(field.value)}
                disabled={disabled}
                onCheckedChange={handleToggle}
              />
              一併調整付款計畫
            </label>
          )}
        />

        {totalChanged && !enabled ? (
          <p className="text-sm text-amber-700">
            {`應付總額由 ${formatDisplayAmount(originalTotal)} 變為 ${formatDisplayAmount(nextTotal)}，`}
            提交前需一併調整付款計畫。
          </p>
        ) : null}

        {enabled ? (
          <div className="space-y-3">
            <div className={cn('grid grid-cols-1 gap-3', '@3xl/form-section:grid-cols-2')}>
              <FloatingSelect
                id="paymentPlan.inputMode"
                label="輸入方式"
                options={PAYMENT_PLAN_INPUT_MODE_OPTIONS}
                control={control}
                errors={errors}
                isDisabled={disabled}
              />
              <FloatingTextarea
                id="paymentPlan.changeReason"
                label="計畫調整原因"
                register={register}
                errors={errors}
                rows={1}
                disabled={disabled}
              />
            </div>

            <ul className="m-0 list-none space-y-2 p-0">
              {fields.map((field, index) => (
                <li
                  key={field.id}
                  className={cn(
                    'grid grid-cols-1 items-start gap-3 rounded-md border border-gray-200 px-3 pt-3',
                    '@3xl/form-section:grid-cols-[4rem_minmax(10rem,1fr)_minmax(8rem,1fr)_minmax(9rem,1fr)_2.5rem]',
                  )}
                >
                  <p className="pt-3 text-sm font-medium text-gray-700">{`第 ${index + 1} 期`}</p>
                  <FloatingSelect
                    id={`paymentPlan.phases.${index}.phaseId`}
                    label="承接期別"
                    options={phaseOptions}
                    control={control}
                    errors={errors}
                    isDisabled={disabled}
                  />
                  {isPercentage ? (
                    <FloatingInput
                      id={`paymentPlan.phases.${index}.percentage`}
                      label="百分比（%）"
                      type="number"
                      min={0}
                      step="any"
                      register={register}
                      errors={errors}
                      disabled={disabled}
                    />
                  ) : (
                    <FloatingInput
                      id={`paymentPlan.phases.${index}.amount`}
                      label="金額"
                      type="number"
                      min={0}
                      step="any"
                      register={register}
                      errors={errors}
                      disabled={disabled}
                    />
                  )}
                  <FloatingDateInput
                    id={`paymentPlan.phases.${index}.dueDate`}
                    label="到期日（選填）"
                    control={control}
                    errors={errors}
                    disabled={disabled}
                  />
                  <div className="flex pt-1 @3xl/form-section:justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                      aria-label={`移除第 ${index + 1} 期`}
                      disabled={disabled || fields.length <= 1}
                      onClick={() => remove(index)}
                    >
                      <PiTrashBold className="size-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={disabled}
                  onClick={() => append(createEmptyPaymentPlanPhase())}
                >
                  新增期別
                </Button>
                {currentPlan?.phases?.length ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    onClick={handleResetFromCurrent}
                  >
                    以目前計畫重設
                  </Button>
                ) : null}
              </div>
              <p
                className={cn(
                  'text-sm tabular-nums',
                  summary.matched ? 'text-gray-600' : 'font-medium text-rose-600',
                )}
              >
                {isPercentage
                  ? `合計 ${summary.sum}% ／ 需為 100%`
                  : `合計 ${formatDisplayAmount(summary.sum)} ／ 新應付總額 ${formatDisplayAmount(summary.target)}`}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              已付款的原期別必須承接剛好一次；減額造成的待退款由系統計算，送審後可在預覽中查看。
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
};

PurchaseAmendmentPaymentPlanEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  currentPlan: PropTypes.shape({
    paymentDataStatus: PropTypes.string,
    phases: PropTypes.arrayOf(PropTypes.object),
  }),
  currentPlanError: PropTypes.string,
  originalTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nextTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
};

export default PurchaseAmendmentPaymentPlanEditor;
